import React, {useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useQuery, useMutation, useQueryClient} from 'react-query'
import {useTable, useFilters, useGlobalFilter, useSortBy} from 'react-table'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import clsx from 'clsx'
import Swal from 'sweetalert2'
import {KTIcon} from '../../../../helpers'
import {useLayout} from '../../../../layout/core'
import RejectionReasonModal from '../modal/RejectionReasonModal'
import {DrawerSenderDetails} from '../drawers/DrawerSenderDetails'
import {
  fetchSenderList,
  patchSenderStatus,
  SENDER_QUERY_KEY,
} from '../../../../../app/pages/senderApproval/_requests'

dayjs.extend(localizedFormat)

type Props = {
  className: string
}

const TablesWidgetSenderApproval: React.FC<Props> = ({className}) => {
  const intl = useIntl()
  const {showLoader, hideLoader} = useLayout()
  const queryClient = useQueryClient()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [senderDrawerRecord, setSenderDrawerRecord] = useState<any>(null)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve')
  const [selectedUuid, setSelectedUuid] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const {data, isFetching} = useQuery(
    [SENDER_QUERY_KEY, page, pageSize, debouncedSearch],
    () =>
      fetchSenderList({
        page,
        page_size: pageSize,
        ...(debouncedSearch ? {search: debouncedSearch} : {}),
      }),
    {keepPreviousData: true}
  )

  const decisionMutation = useMutation(
    ({id, active, reason}: {id: string; active: boolean; reason: string}) =>
      patchSenderStatus(id, active, reason),
    {
      onMutate: () => showLoader(),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(SENDER_QUERY_KEY)
        Swal.fire({
          icon: 'success',
          title: variables.active
            ? intl.formatMessage({id: 'ALERT.SENDER_APPROVED', defaultMessage: 'Sender Approved'})
            : intl.formatMessage({id: 'ALERT.SENDER_REJECTED', defaultMessage: 'Sender Rejected'}),
          timer: 1500,
          showConfirmButton: false,
        })
      },
      onError: (err) => console.error(err),
      onSettled: () => {
        hideLoader()
        setShowRejectModal(false)
      },
    }
  )

  const allRows = data?.results ?? []
  const totalCount = data?.count ?? 0

  const pageCount = Math.ceil(totalCount / pageSize) || 1

  const handleModalSubmit = (reason: string) => {
    decisionMutation.mutate({
      id: selectedUuid,
      active: actionType === 'approve',
      reason,
    })
  }

  const columns = useMemo(
    () => [
      {
        Header: intl.formatMessage({id: 'TABLE.SL_NO', defaultMessage: 'S.No'}),
        id: 'serial-number',
        Cell: ({row}: any) => (page - 1) * pageSize + row.index + 1,
      },
      {
        Header: intl.formatMessage({id: 'TABLE.SENDER_NAME', defaultMessage: 'Sender Name'}),
        accessor: 'name',
        Cell: ({value}: any) => <span className='fw-bold text-gray-800'>{value}</span>,
      },
      {
        Header: intl.formatMessage({id: 'TABLE.OWNER_ID', defaultMessage: 'Organisation ID'}),
        accessor: 'ownerId',
        Cell: ({value}: any) => <span className='text-gray-800'>{value ?? '—'}</span>,
      },
      {
        Header: intl.formatMessage({id: 'TABLE.REGISTERED_ON', defaultMessage: 'Registered On'}),
        accessor: 'created_at',
        Cell: ({value}: any) => (
          <>
            <span>{dayjs(value).format('DD/MM/YYYY')}</span>
            <span className='d-block text-muted fw-semibold'>{dayjs(value).format('LT')}</span>
          </>
        ),
      },
      {
        Header: intl.formatMessage({id: 'TABLE.STATUS', defaultMessage: 'Status'}),
        accessor: 'status',
        Cell: ({value}: any) =>
          value === 'approved' ? (
            <span className='badge badge-light-success fw-bold px-4 py-3'>
              {intl.formatMessage({id: 'COMMON.APPROVED', defaultMessage: 'Approved'})}
            </span>
          ) : value === 'rejected' ? (
            <span className='badge badge-light-danger fw-bold px-4 py-3'>
              {intl.formatMessage({id: 'COMMON.REJECTED', defaultMessage: 'Rejected'})}
            </span>
          ) : (
            <span className='badge badge-light-warning fw-bold px-4 py-3'>
              {intl.formatMessage({id: 'COMMON.PENDING', defaultMessage: 'Pending'})}
            </span>
          ),
      },
      {
        Header: intl.formatMessage({id: 'TABLE.ACTIONS', defaultMessage: 'Actions'}),
        id: 'actions',
        Cell: ({row}: any) => {
          const status = row.original.status
          return (
            <div className='d-flex'>
              <button
                type='button'
                className='btn h-35px w-65px btn-light-info me-3 text-nowrap px-0 py-0'
                id='sender-details-toggle'
                onClick={() => setSenderDrawerRecord(row.original)}
              >
                {intl.formatMessage({id: 'TABLE.VIEW', defaultMessage: 'View'})}
              </button>
              <button
                type='button'
                className={clsx(
                  'btn h-35px btn-icon btn-light-primary me-3',
                  status === 'approved' && 'disabled btn-secondary'
                )}
                disabled={status === 'approved'}
                onClick={() => {
                  setSelectedUuid(row.original.uuid)
                  setActionType('approve')
                  setShowRejectModal(true)
                }}
              >
                <KTIcon
                  iconName='check'
                  className={clsx(
                    'text-hover-white fs-1',
                    status === 'approved' ? 'text-gray' : 'text-primary'
                  )}
                />
              </button>
              <button
                type='button'
                className={clsx(
                  'btn h-35px btn-icon btn-light-danger',
                  status === 'rejected' && 'disabled btn-secondary'
                )}
                disabled={status === 'rejected'}
                onClick={() => {
                  setSelectedUuid(row.original.uuid)
                  setActionType('reject')
                  setShowRejectModal(true)
                }}
              >
                <KTIcon
                  iconName='cross'
                  className={clsx(
                    'text-hover-white fs-1',
                    status === 'rejected' ? 'text-gray' : 'text-danger'
                  )}
                />
              </button>
            </div>
          )
        },
      },
    ],
    [page, pageSize, intl]
  )

  const table = useTable(
    {
      columns,
      data: allRows,
      autoResetFilters: false,
      autoResetGlobalFilter: false,
      autoResetSortBy: false,
    } as any,
    useFilters,
    useGlobalFilter,
    useSortBy
  )

  return (
    <div className={`card ${className}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>
            {intl.formatMessage({id: 'SENDER.TITLE', defaultMessage: 'Sender Approval'})}
          </span>
          <span className='text-muted fw-semibold fs-7'>
            {intl.formatMessage(
              {id: 'SENDER.TOTAL_SENDERS', defaultMessage: 'Total {count} senders'},
              {count: totalCount}
            )}
          </span>
        </h3>
        <div className='d-flex align-items-center'>
          <div className='d-flex align-items-center position-relative my-1 ms-2'>
            <KTIcon iconName='magnifier' className='text-white-500 position-absolute ms-2' />
            <input
              type='text'
              className='form-control form-control-solid form-select-sm w-150px ps-9'
              placeholder={intl.formatMessage({
                id: 'SENDER.SEARCH',
                defaultMessage: 'Search sender name',
              })}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
            />
          </div>
        </div>
      </div>

      <div className='card-body pt-3'>
        <div className='table-responsive'>
          <table
            {...table.getTableProps()}
            className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
          >
            <thead>
              {table.headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps((column as any).getSortByToggleProps())}>
                      <span className='text-gray-400 fs-7'>{column.render('Header')}</span>
                      {(column as any).isSorted ? (
                        (column as any).isSortedDesc ? (
                          <KTIcon iconName='down' className='ms-1' />
                        ) : (
                          <KTIcon iconName='up' className='ms-1' />
                        )
                      ) : (
                        ''
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...table.getTableBodyProps()}>
              {isFetching ? (
                <tr>
                  <td colSpan={columns.length} className='text-center py-6'>
                    <span className='spinner-border spinner-border-sm text-primary' />
                  </td>
                </tr>
              ) : table.rows.length > 0 ? (
                table.rows.map((row) => {
                  table.prepareRow(row)
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td className='fw-bold' {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={columns.length} className='text-center'>
                    <span className='fw-bold'>
                      {intl.formatMessage({
                        id: 'COMMON.NO_DATA_FOUND',
                        defaultMessage: 'No data found.',
                      })}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className='d-flex justify-content-between align-items-center mt-5'>
            <div>
              <select
                className='form-select form-select-sm form-select-solid'
                value={pageSize}
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value))
                  setPage(1)
                }}
              >
                {[10, 25, 50, 100].map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </div>
            <div>
              <span>
                Page{' '}
                <strong>
                  {page} of {pageCount}
                </strong>
              </span>
            </div>
            <div>
              <button
                className='btn btn-sm'
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
              >
                <KTIcon iconName='left' className='text-gray fs-1 me-5' />
              </button>
              <button
                className='btn btn-sm'
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= pageCount}
              >
                <KTIcon iconName='right' className='text-gray fs-1 me-5' />
              </button>
            </div>
          </div>
        </div>
      </div>

      <DrawerSenderDetails
        senderRecord={senderDrawerRecord}
        onApprove={() => {
          setSelectedUuid(senderDrawerRecord?.uuid)
          setActionType('approve')
          setShowRejectModal(true)
        }}
        onReject={() => {
          setSelectedUuid(senderDrawerRecord?.uuid)
          setActionType('reject')
          setShowRejectModal(true)
        }}
      />

      {showRejectModal && (
        <RejectionReasonModal
          show={showRejectModal}
          onHide={() => setShowRejectModal(false)}
          onSubmit={handleModalSubmit}
          title={
            actionType === 'approve'
              ? intl.formatMessage(
                  {id: 'DRAWER.REASON_FOR_APPROVAL', defaultMessage: 'Enter Reason for Approval'}
                )
              : intl.formatMessage(
                  {id: 'DRAWER.REASON_FOR_REJECTION', defaultMessage: 'Enter Reason for Rejection'}
                )
          }
          placeholder={
            actionType === 'approve'
              ? intl.formatMessage({
                  id: 'DRAWER.APPROVAL_REASON_PLACEHOLDER',
                  defaultMessage: 'Enter approval reason',
                })
              : intl.formatMessage({
                  id: 'DRAWER.REJECTION_REASON_PLACEHOLDER',
                  defaultMessage: 'Enter rejection reason',
                })
          }
        />
      )}
    </div>
  )
}

export {TablesWidgetSenderApproval}
